import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { ContactSchema, type Contact } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Depuis le chantier `contact-type`, `contact` porte le discriminant `type`
 * comme les 55 autres familles (`person` | `organization`, vocabulaire ferme,
 * verrouille par les Security Rules). `list({ type })` et `count({ type })`
 * filtrent donc COTE SERVEUR. Piege de migration a connaitre : un document
 * cree avant ce champ n'est PAS rattrape par `where('type', '==', ...)` tant
 * que `scripts/backfill-contact-type.mjs` n'a pas pose la valeur en base.
 */
type ContactListOptions = ListOptions;
type ContactCountOptions = CountOptions;

/**
 * Champs standard et générés par le système : l'appelant ne les fournit jamais
 * à la création, le SDK les remplit automatiquement.
 */
export type ContactCreateInput = Omit<
  Contact,
  'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy' | 'archivedAt' | 'deletedAt' | 'schemaVersion'
>;

/**
 * Patch de mise à jour d'un contact (update partiel).
 * `orgId` et `type` sont immuables (Security Rules : on ne transforme pas une
 * personne en organisation, on crée l'organisation) ; les champs standard sont
 * gérés par le SDK (`updatedAt`/`updatedBy` remplis automatiquement) : aucun
 * des trois n'est accepté ici.
 */
export type ContactUpdateInput = Partial<
  Omit<
    Contact,
    | 'id'
    | 'orgId'
    | 'type'
    | 'createdAt'
    | 'updatedAt'
    | 'createdBy'
    | 'updatedBy'
    | 'archivedAt'
    | 'deletedAt'
    | 'schemaVersion'
  >
>;

/** Nom de la collection Firestore des contacts (entité cœur, org-wide). */
export const CONTACTS_COLLECTION = 'contacts';

/**
 * Repository typé pour l'entité `contact`.
 * Toute lecture est validée par `ContactSchema` (cage IA : aucun accès Firestore
 * direct hors SDK, toute donnée traverse le modèle canonique).
 */
export interface ContactsRepository {
  create(input: ContactCreateInput): Promise<Contact>;
  update(id: string, patch: ContactUpdateInput): Promise<Contact>;
  list(options?: ContactListOptions): Promise<Contact[]>;
  count(options?: ContactCountOptions): Promise<number>;
  get(id: string): Promise<Contact | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Contact>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Contact>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Contact>;
}

const contactConverter = createConverter<Contact>((raw: DocumentData) => ContactSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk` depuis Firebase Auth), 'system' si absent.
 *   Les Security Rules exigent createdBy/updatedBy == request.auth.uid.
 */
export function createContactsRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): ContactsRepository {
  const col = collection(db, CONTACTS_COLLECTION).withConverter(contactConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Contact>(db, col, CONTACTS_COLLECTION, getActorId);

  return {
    async create(input: ContactCreateInput): Promise<Contact> {
      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Payload Firestore : champs métier + champs standard auto-remplis.
      // On utilise serverTimestamp() pour l'horodatage faisant autorité.
      // `type` est TOUJOURS écrit (les rules l'exigent à la création) ; les
      // champs d'identité sont conditionnels, une organisation n'a pas de
      // prénom et une personne n'a pas de raison sociale.
      const payload: DocumentData = {
        type: input.type,
        ...(input.name !== undefined ? { name: input.name } : {}),
        ...(input.firstName !== undefined ? { firstName: input.firstName } : {}),
        ...(input.lastName !== undefined ? { lastName: input.lastName } : {}),
        ...(input.email !== undefined ? { email: input.email } : {}),
        ...(input.phone !== undefined ? { phone: input.phone } : {}),
        ...(input.customFields !== undefined ? { customFields: input.customFields } : {}),
        orgId: input.orgId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Contact);

      // setDoc ne renvoie pas le document résolu (serverTimestamp non résolu) :
      // on valide localement une projection cohérente avec ce qui a été écrit.
      return ContactSchema.parse({
        id: ref.id,
        type: input.type,
        ...(input.name !== undefined ? { name: input.name } : {}),
        ...(input.firstName !== undefined ? { firstName: input.firstName } : {}),
        ...(input.lastName !== undefined ? { lastName: input.lastName } : {}),
        ...(input.email !== undefined ? { email: input.email } : {}),
        ...(input.phone !== undefined ? { phone: input.phone } : {}),
        ...(input.customFields !== undefined ? { customFields: input.customFields } : {}),
        orgId: input.orgId,
        createdAt: now,
        updatedAt: now,
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      });
    },

    async update(id: string, patch: ContactUpdateInput): Promise<Contact> {
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel).
      // customFields est eclate en chemins pointes ('customFields.<cle>') pour
      // fusionner cle par cle au lieu de remplacer la map entiere
      // (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(patch);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, CONTACTS_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec ContactSchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`contact ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ContactListOptions): Promise<Contact[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: ContactCountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Contact | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
